/**
 * Coinbase Signal Charts — read-only visualization of APGE grades,
 * intelligence scores, and cycle distribution. Uses Recharts with MUI dark theme.
 */

import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { CoinbaseDecisionLabPayload } from '../../services/coinbaseService';

// ─── Grade encoding ────────────────────────────────────────────────────────────

const GRADE_RANK: Record<string, number> = {
  A_PLUS: 5, A: 4, B: 3, C: 2, D: 1, F: 0,
};

const RANK_LABEL: Record<number, string> = {
  5: 'A+', 4: 'A', 3: 'B', 2: 'C', 1: 'D', 0: 'F',
};

const PRODUCT_PALETTE = ['#4fc3f7', '#f48fb1', '#81c784', '#ffb74d'];

// ─── Helpers ───────────────────────────────────────────────────────────────────

function gradeBarColor(grade: string, successColor: string, infoColor: string, errorColor: string, dimColor: string): string {
  if (grade === 'A_PLUS' || grade === 'A') return successColor;
  if (grade === 'B') return infoColor;
  if (grade === 'F') return errorColor;
  return dimColor;
}

// ─── Custom dark tooltips ──────────────────────────────────────────────────────

interface TooltipPayloadItem {
  name: string;
  value: number;
  color: string;
}

function DarkTooltip({ active, payload, label }: { active?: boolean; payload?: TooltipPayloadItem[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 12px', fontSize: 11 }}>
      <div style={{ color: '#888', marginBottom: 4 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color, display: 'flex', gap: 8, marginBottom: 2 }}>
          <span>{p.name}</span>
          <span style={{ fontWeight: 700 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

function GradeTooltip({ active, payload, label }: { active?: boolean; payload?: TooltipPayloadItem[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '8px 12px', fontSize: 11 }}>
      <div style={{ color: '#888', marginBottom: 4 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color, display: 'flex', gap: 8, marginBottom: 2 }}>
          <span>{p.name}</span>
          <span style={{ fontWeight: 700 }}>{RANK_LABEL[p.value] ?? p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Chart card shell ──────────────────────────────────────────────────────────

function ChartCard({ title, height = 200, children }: { title: string; height?: number; children: React.ReactNode }) {
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', display: 'block', mb: 1.5 }}
        >
          {title}
        </Typography>
        <Box sx={{ height }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function CoinbaseIntelCharts({ payload }: { payload: CoinbaseDecisionLabPayload }) {
  const theme = useTheme();
  const pf = payload.paperForward;
  const intel = payload.intelligence ?? [];

  const tickStyle = { fill: theme.palette.text.secondary, fontSize: 11 };
  const axisLineStyle = { stroke: theme.palette.divider };

  // APGE grade history — wide format for multi-product lines
  const { gradeData, gradeProducts } = useMemo(() => {
    if (!pf?.recentCycles?.length) return { gradeData: [], gradeProducts: [] };
    const cycles = [...pf.recentCycles].slice(-80);
    const products = [...new Set(cycles.map(c => c.productId))];
    const byTime = new Map<string, Record<string, number | string>>();
    for (const c of cycles) {
      if (!c.apgeGrade) continue;
      const key = new Date(c.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      if (!byTime.has(key)) byTime.set(key, { time: key });
      const row = byTime.get(key)!;
      row[c.productId] = GRADE_RANK[c.apgeGrade] ?? -1;
    }
    return { gradeData: [...byTime.values()], gradeProducts: products };
  }, [pf]);

  // Grade distribution from metrics
  const gradeDistData = useMemo(() => {
    if (!pf?.metrics?.gradeCounts) return [];
    return Object.entries(pf.metrics.gradeCounts)
      .sort((a, b) => (GRADE_RANK[b[0]] ?? -1) - (GRADE_RANK[a[0]] ?? -1))
      .map(([grade, count]) => ({ grade, count }));
  }, [pf]);

  // Intelligence scores: tomi + kaze per product
  const scoreData = useMemo(() => {
    return intel
      .filter(i => i.tomiScore !== undefined || i.kazeScore !== undefined)
      .map(i => ({
        product: i.productId.split('-')[0],
        tomi: typeof i.tomiScore === 'number' ? Math.round(i.tomiScore * 1000) / 1000 : 0,
        kaze: typeof i.kazeScore === 'number' ? Math.round(i.kazeScore * 1000) / 1000 : 0,
      }));
  }, [intel]);

  // Chart grade signals: apge + chart grade per product for grouped bar
  const gradeSignalData = useMemo(() => {
    return intel
      .filter(i => i.apge?.grade || i.chart?.grade)
      .map(i => ({
        product: i.productId.split('-')[0],
        apge: GRADE_RANK[i.apge?.grade ?? ''] ?? null,
        chart: GRADE_RANK[i.chart?.grade ?? ''] ?? null,
        apgeLabel: i.apge?.grade ?? '—',
        chartLabel: i.chart?.grade ?? '—',
      }));
  }, [intel]);

  const hasGradeHistory = gradeData.length > 1;
  const hasDistribution = gradeDistData.length > 0;
  const hasScores = scoreData.some(s => s.tomi !== 0 || s.kaze !== 0);
  const hasGradeSignals = gradeSignalData.length > 0;

  if (!hasGradeHistory && !hasDistribution && !hasScores && !hasGradeSignals) return null;

  return (
    <Box>
      {/* Row 1: APGE grade history (step chart) + grade distribution */}
      <Grid container spacing={2}>
        {hasGradeHistory && (
          <Grid item xs={12} md={hasDistribution ? 8 : 12}>
            <ChartCard title="APGE GRADE HISTORY" height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={gradeData} margin={{ top: 4, right: 12, bottom: 0, left: -16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                  <XAxis
                    dataKey="time"
                    tick={tickStyle}
                    axisLine={axisLineStyle}
                    tickLine={axisLineStyle}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={tickStyle}
                    axisLine={axisLineStyle}
                    tickLine={axisLineStyle}
                    domain={[0, 5]}
                    ticks={[0, 1, 2, 3, 4, 5]}
                    tickFormatter={(v: number) => RANK_LABEL[v] ?? ''}
                  />
                  <Tooltip content={<GradeTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11, color: theme.palette.text.secondary }} />
                  {gradeProducts.map((prod, i) => (
                    <Line
                      key={prod}
                      type="stepAfter"
                      dataKey={prod}
                      stroke={PRODUCT_PALETTE[i % PRODUCT_PALETTE.length]}
                      dot={false}
                      strokeWidth={2}
                      connectNulls={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        )}

        {hasDistribution && (
          <Grid item xs={12} md={hasGradeHistory ? 4 : 6}>
            <ChartCard title="GRADE DISTRIBUTION" height={220}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={gradeDistData} margin={{ top: 4, right: 12, bottom: 0, left: -16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                  <XAxis dataKey="grade" tick={tickStyle} axisLine={axisLineStyle} tickLine={axisLineStyle} />
                  <YAxis tick={tickStyle} axisLine={axisLineStyle} tickLine={axisLineStyle} allowDecimals={false} />
                  <Tooltip content={<DarkTooltip />} />
                  <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                    {gradeDistData.map((entry) => (
                      <Cell
                        key={entry.grade}
                        fill={gradeBarColor(
                          entry.grade,
                          theme.palette.success.main,
                          theme.palette.info.main,
                          theme.palette.error.main,
                          theme.palette.text.disabled,
                        )}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        )}
      </Grid>

      {/* Row 2: Current APGE + Chart grades + Signal scores */}
      <Grid container spacing={2}>
        {hasGradeSignals && (
          <Grid item xs={12} md={hasScores ? 6 : 12}>
            <ChartCard title="CURRENT GRADES (APGE · CHART)" height={160}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={gradeSignalData} margin={{ top: 4, right: 12, bottom: 0, left: -16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                  <XAxis dataKey="product" tick={tickStyle} axisLine={axisLineStyle} tickLine={axisLineStyle} />
                  <YAxis
                    tick={tickStyle}
                    axisLine={axisLineStyle}
                    tickLine={axisLineStyle}
                    domain={[0, 5]}
                    ticks={[0, 1, 2, 3, 4, 5]}
                    tickFormatter={(v: number) => RANK_LABEL[v] ?? ''}
                  />
                  <Tooltip content={<GradeTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11, color: theme.palette.text.secondary }} />
                  <Bar dataKey="apge" name="APGE" fill={PRODUCT_PALETTE[0]} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="chart" name="Chart" fill={PRODUCT_PALETTE[1]} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        )}

        {hasScores && (
          <Grid item xs={12} md={hasGradeSignals ? 6 : 12}>
            <ChartCard title="SIGNAL SCORES (TOMI · KAZE)" height={160}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={scoreData}
                  layout="vertical"
                  margin={{ top: 4, right: 12, bottom: 0, left: 32 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} horizontal={false} />
                  <XAxis
                    type="number"
                    tick={tickStyle}
                    axisLine={axisLineStyle}
                    tickLine={axisLineStyle}
                    domain={[-1, 1]}
                    ticks={[-1, -0.5, 0, 0.5, 1]}
                  />
                  <YAxis
                    type="category"
                    dataKey="product"
                    tick={tickStyle}
                    axisLine={axisLineStyle}
                    tickLine={axisLineStyle}
                    width={36}
                  />
                  <Tooltip content={<DarkTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11, color: theme.palette.text.secondary }} />
                  <Bar dataKey="tomi" name="Tomi" fill={PRODUCT_PALETTE[0]} radius={[0, 3, 3, 0]} />
                  <Bar dataKey="kaze" name="Kaze" fill={PRODUCT_PALETTE[1]} radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
